import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Sparkles, ArrowRight, Loader2, Check, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Public /free-audit — self-serve mini-audit (3 queries). Funnels signups into the CRM. */
export default function PublicFreeAudit() {
  const [form, setForm] = useState({ business_name: "", industry: "", city: "", email: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.business_name || !form.industry || !form.city || !(form.email || form.phone)) {
      return setErr("Please fill business name, industry, city, and email or phone.");
    }
    setErr(""); setBusy(true); setResult(null);
    try {
      const { data } = await axios.post(`${API}/audit/free`, form);
      if (data.pending) {
        // Perplexity key not configured server-side — still show success so
        // the lead is captured, but message points to manual follow-up.
        setResult({ pending: true, message: data.message, score: { hits: 0, total: 3 }, share_url: null });
      } else {
        setResult(data);
      }
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Something went wrong — please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#FFF6EE] text-[#0A192F]" data-testid="free-audit-page">
      <div className="border-b border-[#E5D9CB] bg-white/70 backdrop-blur">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="font-display font-black text-lg tracking-tight no-underline text-[#0A192F]">
            Clients<span className="text-[#E24E1B]">OnRamp</span>
          </Link>
          <Link to="/book" className="text-sm text-neutral-600 hover:text-black no-underline">Book a call →</Link>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-16">
        <div className="inline-flex items-center gap-2 bg-white border border-[#E5D9CB] px-3 py-1.5 text-xs font-mono uppercase tracking-widest mb-6">
          <Sparkles className="w-3.5 h-3.5 text-[#E24E1B]" /> Free mini-audit · 3 real queries
        </div>
        <h1 className="font-display text-5xl font-black tracking-tighter mb-3 leading-[1.05]">
          See if AI search actually recommends your business.
        </h1>
        <p className="text-neutral-600 text-lg mb-10 max-w-2xl">
          We'll run 3 real queries through Perplexity's web-connected AI engine and show
          you exactly whether you appear when locals search — in 15 seconds. No credit card.
        </p>

        {!result ? (
          <form onSubmit={submit} className="bg-white border border-[#E5D9CB] p-6 space-y-4 relative" data-testid="free-audit-form">
            <Link
              to="/"
              aria-label="Close and return home"
              data-testid="free-audit-close"
              className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center text-neutral-500 hover:text-white hover:bg-[#E24E1B] border border-neutral-200 hover:border-[#E24E1B] transition rounded-full no-underline"
              title="Close"
            >
              <X className="w-4 h-4" />
            </Link>
            {["business_name", "industry", "city"].map((f) => (
              <label key={f} className="block">
                <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-1.5">
                  {f === "business_name" ? "Business name" : f === "industry" ? "Industry (e.g. plumber, dentist)" : "City / area"}
                </div>
                <input value={form[f]} onChange={(e) => setForm({ ...form, [f]: e.target.value })}
                  className="w-full px-3 py-3 border border-neutral-300 bg-white focus:outline-none focus:border-black"
                  data-testid={`free-audit-${f}`} />
              </label>
            ))}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="block">
                <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-1.5">Email</div>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-3 py-3 border border-neutral-300 bg-white focus:outline-none focus:border-black"
                  data-testid="free-audit-email" />
              </label>
              <label className="block">
                <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-1.5">Phone (optional)</div>
                <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full px-3 py-3 border border-neutral-300 bg-white focus:outline-none focus:border-black"
                  data-testid="free-audit-phone" />
              </label>
            </div>
            {err && <div className="p-3 bg-rose-50 border border-rose-200 text-rose-800 text-sm" data-testid="free-audit-error">{err}</div>}
            <button type="submit" disabled={busy}
              className="w-full bg-[#E24E1B] text-white font-semibold py-4 hover:bg-[#0A192F] transition inline-flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="free-audit-submit">
              {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Running your 3-query audit…</> : <>Run my free audit <ArrowRight className="w-4 h-4" /></>}
            </button>
            <p className="text-xs text-neutral-500 text-center">By running the audit you agree to receive a follow-up email with your full report + next steps. Unsubscribe anytime.</p>
          </form>
        ) : (
          <div className="bg-white border border-[#E24E1B] p-8" data-testid="free-audit-result">
            {result.pending ? (
              <>
                <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-2">Thanks — you're on the list</div>
                <div className="font-display text-4xl font-black text-[#E24E1B] leading-tight mb-4">
                  Your audit is queued.
                </div>
                <p className="text-lg mb-6">{result.message}</p>
                <Link to="/book" className="lf-btn-primary text-center no-underline flex items-center justify-center gap-2">
                  Book a 20-min strategy call →
                </Link>
                <p className="text-xs text-neutral-500 mt-6">
                  We got your info at <strong>{form.email}</strong>. Check that inbox in ~15 minutes for your full report.
                </p>
              </>
            ) : (
              <>
                <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-2">Your AI Visibility Score</div>
                <div className="font-display text-7xl font-black text-[#E24E1B] leading-none mb-4">
                  {result.score.hits}<span className="text-3xl text-neutral-400">/{result.score.total}</span>
                </div>
                <p className="text-lg mb-6">
                  We ran 3 real queries. You were mentioned in <strong>{result.score.hits} of 3</strong>.
                  {result.score.hits === 0 && " Your business is essentially invisible to AI-powered search right now."}
                  {result.score.hits === 1 && " Better than nothing, but you're leaving 66% of AI-search leads on the table."}
                  {result.score.hits === 2 && " Solid — but there's a clear gap where competitors are showing up and you're not."}
                  {result.score.hits === 3 && " Excellent — you're already visible. Book a call to compound the lead."}
                </p>
                <div className="flex flex-col md:flex-row gap-3">
                  {result.share_url && (
                    <Link to={result.share_url} target="_blank" className="lf-btn-outline text-center no-underline flex items-center justify-center gap-2">
                      See your full report →
                    </Link>
                  )}
                  <Link to="/book" className="lf-btn-primary text-center no-underline flex items-center justify-center gap-2">
                    Book a 20-min strategy call →
                  </Link>
                </div>
                <p className="text-xs text-neutral-500 mt-6">
                  We're also running the <strong>full 10-query audit</strong> right now — the version we normally sell for $497 — and it will hit <strong>{form.email}</strong> within 15 minutes. Check spam if it doesn't arrive.
                </p>
              </>
            )}
          </div>
        )}

        <div className="mt-16 grid grid-cols-3 gap-6 text-center">
          {[
            { title: "3 real queries", desc: "Same searches your customers make" },
            { title: "Live citations", desc: "See exactly which sources AI trusts" },
            { title: "Zero risk", desc: "No credit card. Delivered in 15 seconds." },
          ].map((s, i) => (
            <div key={i}>
              <div className="w-10 h-10 mx-auto mb-3 bg-[#0A192F] text-white flex items-center justify-center font-display font-black">{i + 1}</div>
              <div className="font-semibold mb-1">{s.title}</div>
              <div className="text-xs text-neutral-500">{s.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
